import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * Onboarding Store
 *
 * Tracks onboarding tasks for players who have joined a campaign
 * but haven't completed setup steps like creating a character.
 *
 * Tasks are stored per-campaign to handle multiple campaigns.
 */

// =============================================================================
// TYPES
// =============================================================================

export type TaskId =
  | 'create_character'
  | 'set_avatar'
  | 'review_rules';

export interface OnboardingTask {
  id: TaskId;
  title: string;
  description: string;
  completed: boolean;
  priority: number; // Lower = higher priority
}

interface CampaignTasks {
  [taskId: string]: boolean; // true = completed
}

interface OnboardingState {
  // Tasks per campaign: { campaignId: { taskId: completed } }
  campaignTasks: Record<string, CampaignTasks>;

  // Actions
  initCampaignTasks: (campaignId: string) => void;
  completeTask: (campaignId: string, taskId: TaskId) => void;
  resetTask: (campaignId: string, taskId: TaskId) => void;

  // Selectors
  getTasks: (campaignId: string) => OnboardingTask[];
  hasIncompleteTasks: (campaignId: string) => boolean;
  getIncompleteTaskCount: (campaignId: string) => number;
}

// =============================================================================
// TASK DEFINITIONS
// =============================================================================

const TASK_DEFINITIONS: Omit<OnboardingTask, 'completed'>[] = [
  {
    id: 'create_character',
    title: 'Create Your Character',
    description: 'Build your hero to join the adventure',
    priority: 1,
  },
  // Future tasks:
  // {
  //   id: 'set_avatar',
  //   title: 'Set Your Avatar',
  //   description: 'Choose a token image for your character',
  //   priority: 2,
  // },
  // {
  //   id: 'review_rules',
  //   title: 'Review House Rules',
  //   description: 'Read the campaign rules and guidelines',
  //   priority: 3,
  // },
];

// =============================================================================
// STORE
// =============================================================================

export const useOnboardingStore = create<OnboardingState>()(
  persist(
    (set, get) => ({
      campaignTasks: {},

      initCampaignTasks: (campaignId: string) => {
        const { campaignTasks } = get();

        // Skip if already initialized
        if (campaignTasks[campaignId]) return;

        // Initialize all tasks as incomplete
        const tasks: CampaignTasks = {};
        TASK_DEFINITIONS.forEach((task) => {
          tasks[task.id] = false;
        });

        set({
          campaignTasks: {
            ...campaignTasks,
            [campaignId]: tasks,
          },
        });
      },

      completeTask: (campaignId: string, taskId: TaskId) => {
        const { campaignTasks } = get();
        const tasks = campaignTasks[campaignId] || {};

        set({
          campaignTasks: {
            ...campaignTasks,
            [campaignId]: {
              ...tasks,
              [taskId]: true,
            },
          },
        });
      },

      resetTask: (campaignId: string, taskId: TaskId) => {
        const { campaignTasks } = get();
        const tasks = campaignTasks[campaignId] || {};

        set({
          campaignTasks: {
            ...campaignTasks,
            [campaignId]: {
              ...tasks,
              [taskId]: false,
            },
          },
        });
      },

      getTasks: (campaignId: string): OnboardingTask[] => {
        const { campaignTasks } = get();
        const tasks = campaignTasks[campaignId] || {};

        return TASK_DEFINITIONS.map((def) => ({
          ...def,
          completed: tasks[def.id] || false,
        })).sort((a, b) => a.priority - b.priority);
      },

      hasIncompleteTasks: (campaignId: string): boolean => {
        const { campaignTasks } = get();
        const tasks = campaignTasks[campaignId];

        if (!tasks) return true; // Not initialized = has tasks

        return TASK_DEFINITIONS.some((def) => !tasks[def.id]);
      },

      getIncompleteTaskCount: (campaignId: string): number => {
        const { campaignTasks } = get();
        const tasks = campaignTasks[campaignId];

        if (!tasks) return TASK_DEFINITIONS.length;

        return TASK_DEFINITIONS.filter((def) => !tasks[def.id]).length;
      },
    }),
    {
      name: 'machina-onboarding',
      partialize: (state) => ({ campaignTasks: state.campaignTasks }),
    }
  )
);
